import { httpClient } from '@/utils/httpClient';
import { formatNomenclatureWithSize } from '@equip/utils';
import type { RequestOptions } from '@/types/api';

export interface CreateReceiptRequest {
  orderLineItemId: string;
  quantityReceived: number;
  receiptDate?: string;
}

export interface CreateAssetsFromReceiptRequest {
  catalogId: string;
  quantityToCreate: number;
  sourceReceiptId: string;
  receivingLocation?: string;
}

export interface ReceiptResponse {
  receiptId: string;
  createdAssetIds: string[];
}

export interface CompleteReceiveOrderRequest {
  orderId: string;
  items: Array<{ lineItemId: string; quantity: number; locationId?: string | null }>;
}

export interface CompleteReceiveOrderResponse {
  orderId: string;
  receiptIds: string[];
  createdAssetIds: string[];
  orderStatus: 'Partially Received' | 'Completed';
  transactionId?: string;
  /** Receipt document number with suffix (e.g., "6033-4001-A") */
  receiptDocumentNumber?: string;
  /** The suffix letter for this receipt (A, B, C, etc.) */
  receiptSuffix?: string;
}

class OrderReceiptService {
  async createReceipt(request: CreateReceiptRequest): Promise<ReceiptResponse> {
    // Create order_receipt entity
    const receiptData = {
      entity_type: 'order_receipt',
      entity_identifier: `RCPT-${Date.now()}`,
      display_name: `Receipt for ${request.orderLineItemId}`,
      status: 'active',
      entity_data: {
        order_line_item_id: request.orderLineItemId,
        quantity_received: request.quantityReceived,
        receipt_date: request.receiptDate ?? new Date().toISOString(),
      },
      metadata: {
        source: 'receive_order_modal',
      },
    };

    const receipt = await httpClient.post<{ id: string }>('/api/v1/entities', receiptData);

    return {
      receiptId: receipt.id,
      createdAssetIds: [],
    };
  }

  async createAssetsFromReceipt(request: CreateAssetsFromReceiptRequest): Promise<string[]> {
    // Fetch catalog details
    const catalog = await httpClient.get<Array<{ entity_data?: { nomenclature?: string; size?: string } }>>(
      `/api/v1/entities?id=eq.${request.catalogId}&select=entity_data`,
    );

    const assetIds: string[] = [];

    // Create individual asset records
    for (let i = 0; i < request.quantityToCreate; i++) {
      const assetData = {
        entity_type: 'asset',
        entity_identifier: `AT-${Date.now()}-${i}`,
        display_name: formatNomenclatureWithSize(
          catalog[0]?.entity_data?.nomenclature as string | undefined ?? 'Asset',
          catalog[0]?.entity_data?.size as string | undefined,
        ),
        status: 'active',
        entity_data: {
          catalog_id: request.catalogId,
          source_receipt_id: request.sourceReceiptId,
          location: request.receivingLocation,
          // Copy relevant catalog fields
        },
        metadata: {
          source: 'order_receipt',
          created_from_receipt: request.sourceReceiptId,
        },
      };

      const asset = await httpClient.post<{ id: string }>('/api/v1/entities', assetData);
      assetIds.push(asset.id);
    }

    return assetIds;
  }

  async completeOrderReceipt(
    request: CompleteReceiveOrderRequest,
    requestOptions?: RequestOptions,
  ): Promise<CompleteReceiveOrderResponse> {
    return httpClient.post<CompleteReceiveOrderResponse>(
      '/api/v1/receive-order/complete',
      request,
      requestOptions,
    );
  }
}

export const orderReceiptService = new OrderReceiptService();
